import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { FaArrowRight } from "react-icons/fa6";


const HeroSection = ({
  title,
  subtitle,
  description,
  highlightedText,
  heroImage,
  bgImg,
  bgColor = 'bg-blue-600',
  btnText,
  btnLink
}) => {
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Initialize GSAP animation context
    const container = containerRef.current;
    const image = imageRef.current;

    if (!container || !image) return;

    // Reset any existing transformations
    gsap.set(image, {
      transformPerspective: 1000,
      transformOrigin: 'center center'
    });

    // Create a smoother initial wiggle animation
    const wiggleTimeline = gsap.timeline({
      defaults: { ease: "sine.inOut" }
    });

    wiggleTimeline
      .to(image, {
        duration: 2,
        rotationY: 1.5,
        rotationX: -1,
        z: 20,
      })
      .to(image, {
        duration: 2,
        rotationY: -1.5,
        rotationX: 0.5,
        z: 10,
      })
      .to(image, {
        duration: 2,
        rotationY: 0,
        rotationX: 0,
        z: 0,
      });

    // Function to handle mouse movement for the 3D effect
    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      // Calculate mouse position relative to the container
      const xPos = (e.clientX - rect.left) / rect.width - 0.5;
      const yPos = (e.clientY - rect.top) / rect.height - 0.5;

      // Apply 3D rotation based on mouse position with smoother animation
      gsap.to(image, {
        duration: 0.8,
        rotationY: xPos * 10, // horizontal tilt
        rotationX: -yPos * 10, // vertical tilt
        ease: "power1.out",
        z: 50, // slight push forward for depth
        boxShadow: `${xPos * -20}px ${yPos * -20}px 20px rgba(0,0,0,0.2)`
      });
    };

    // Function to reset the 3D effect when mouse leaves
    const handleMouseLeave = () => {
      gsap.to(image, {
        duration: 1.2,
        rotationY: 0,
        rotationX: 0,
        z: 0,
        ease: "power2.out",
        boxShadow: "0px 0px 0px rgba(0,0,0,0)"
      });
    };

    // Add event listeners
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    // Cleanup function to remove event listeners
    return () => {
      wiggleTimeline.kill();
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div className={`relative overflow-hidden w-full bg-cover bg-no-repeat bg-center ${bgColor}`} style={{ backgroundImage: `url('${bgImg}')` }}>
      {/* <div className="absolute inset-0 bg-gray-800 opacity-70"></div> */}
      <div className="container max-w-7xl mx-auto px-6 md:py-16 py-16 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          {/* Left side content (Text Area) */}
          <div className="max-w-5xl text-white text-left lg:mb-0">
            <h1 className="text-5xl md:text-7xl font-bold mb-3 leading-tight">{title.bold} <span className='font-normal text-white'>{title.light}</span></h1>
            <p className="text-xl md:text-3xl font-light mb-8 text-white">{subtitle}</p>
          </div>
        </div>
        {/* Full width image section with 3D effect */}
        <div
          ref={containerRef}
          className="rounded-2xl duration-300 cursor-pointer"
        >
          <div className="transform-gpu rounded-2xl">
            <img
              // ref={imageRef}
              src={heroImage}
              alt="Global Expansion Visualization"
              className="w-full md:h-96 rounded-2xl object-cover object-center will-change-transform"
              // style={{
              //   minHeight: '300px',
              //   transformStyle: 'preserve-3d',
              //   backfaceVisibility: 'hidden'
              // }}
            />
          </div>
        </div>
        <div className="mt-8 max-w-5xl text-white text-left lg:mb-0">
          <a className='flex md:hidden mb-6' href={btnLink} target="_blank" rel="noopener noreferrer">
            <button className="bg-orange-600 hover:bg-orange-700 text-white py-3 px-8 text-lg rounded-full flex items-center mx-auto">
              {btnText} <FaArrowRight className='ml-2' />
            </button>
          </a>

          <h2 className="text-xl font-medium tracking-wider">{description.heading}</h2>
          <p className="text-xl leading-relaxed font-light text-gray-100">
            {description.intro}
            <span className="font-medium text-white"> {highlightedText} </span>
            {description.outro}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;